import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { css } from "@emotion/react";
import { useLazyQuery } from "@apollo/react-hooks";

import Input from "core/Components/Form/Input";
import InputWithSelectOptions from "core/Components/Form/InputWithSelectOptions";
import LabelledField from "core/Components/Form/LabelledField";
import PhoneNumberInput from "core/Components/Form/PhoneNumberInput";
import SearchableList from "core/Components/Form/SearchableList";
import { NOOP } from "core/constants";
import { useAppState } from "core/contexts/AppContext";
import useFontSize from "core/hooks/useFontSize";
import { FieldList, OnFieldChange, OptionalOnFieldChangeArgs } from "core/hooks/useForm";
import Countries from "core/includes/countries";
import theme from "theme/index";

import InputWrapper from "pages/Components/CartAndCheckout/components/InputWrapper";
import { ADDITIONAL_FIELD } from "pages/Components/CartAndCheckout/hooks/useDetailsForms";
import { COUNTRIES_WITH_REGION_OPTIONS } from "core/includes/states";
import { getAddress, getSuggestedAddresses } from 'core/includes/queries';
import useCart from "pages/Components/CartPage/hooks/useCart";
import { DEFAULT_COUNTRY_CODE_CONFIG } from "core/includes/constants";
import Loading from "core/Components/Utils/Loading";
import window from "core/includes/window";

type Props = {
    fields: FieldList,
    readOnlyFields?: string[],
    onFieldChange: OnFieldChange,
    errors?: FieldList,
    useBilling?: boolean,
    hasAsterisk?: boolean,
    isReadOnly?: boolean,
    suburbOptions?: string[],
    setSuggestedSuburb?: (city: string) => void,
    clearSuburbOptions?: () => void,
    onAddressFinder: (isUsed: boolean) => void,
    isLoading?: boolean,
}

const DetailsForm = (
    {
        fields,
        readOnlyFields = [],
        errors = {},
        onFieldChange,
        useBilling = false,
        hasAsterisk = false,
        isReadOnly = false,
        suburbOptions = undefined,
        setSuggestedSuburb = NOOP,
        clearSuburbOptions = NOOP,
        onAddressFinder = NOOP,
        isLoading = false,
    }: Props
) => {
    const [showEmptyAddressMessage, setShowEmptyAddressMessage] = useState(false)
    const [fontSize, lineHeight] = useFontSize('label', '18', '18');
    const [appState] = useAppState();
    const addressChangingTimeout = useRef<any>(null);
    const isSearchingAddress = useRef(false);
    const inputRef = useRef<any>(null);
    const { getCountry } = useCart();
    const countryCode = getCountry();
    const timeoutHolder = useRef<any>(null);

    const [getSuggestedAddressesQuery, {
        data: suggestedAddressesData,
        loading: suggestedAddressesLoading
    }] = useLazyQuery(getSuggestedAddresses, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: "network-only",
    });

    const suggestedAddresses = suggestedAddressesData?.suggestedAddresses;

    const [getAddressDetail] = useLazyQuery(getAddress, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: "network-only",
    });

    const filteredCountries = useMemo(() => {
        let notAllowedCountries: string[] = [];
        switch (countryCode.code.toUpperCase()){
            case "AU":
                notAllowedCountries = ['NZ', "GB"];
                break;
            case "NZ":
                notAllowedCountries = ['AU', "GB"];
                break;
            case "GB":
                notAllowedCountries = ['AU', "NZ"];
                break;
            default:
                notAllowedCountries = [];
                break;
        }

        return Countries.filter(country => !notAllowedCountries.includes(country.code));
    }, [countryCode.code]);

    const onSelectedAddressChange = useCallback((value: string, field: string) => {
        const selectedAddressId = suggestedAddresses.find((sa: SuggestedAddress) => (
            sa['full_address'] === value)
        )?.['address_id'];
        onFieldChange(field, value);
        onAddressFinder(true);

        getAddressDetail({
            variables: {
                addressId: selectedAddressId,
                orderCountryCode: appState.cart?.countryCode || DEFAULT_COUNTRY_CODE_CONFIG.code
            }
        }).then(({ data }) => {
            const addressDetail = data?.address;
            if (!addressDetail) return;

            const billingPostfix = useBilling ? 'Billing' : '';
            const extraFields = [
                {
                    name: 'addressTwo' + billingPostfix,
                    value: addressDetail.addressLineTwo,
                },
                {
                    name: 'city' + billingPostfix,
                    value: addressDetail.city,
                },
                {
                    name: 'country' + billingPostfix,
                    value: addressDetail.country,
                },
                {
                    name: 'postCode' + billingPostfix,
                    value: addressDetail.postCode,
                },
            ]

            let stateLabel = addressDetail.state;

            const countriesWithStates = Object.keys(COUNTRIES_WITH_REGION_OPTIONS);
            if (appState.countryCodeByIp && countriesWithStates.includes(appState.countryCodeByIp)) {
                const countryKey = appState.countryCodeByIp as keyof typeof COUNTRIES_WITH_REGION_OPTIONS;
                const supportedStateLabel = COUNTRIES_WITH_REGION_OPTIONS[countryKey]
                    .find((state: { label: string; }) => state.label === addressDetail.state)?.label || '';
                if (supportedStateLabel) {
                    stateLabel = supportedStateLabel;
                }
            }
            extraFields.push({
                name: 'state' + billingPostfix,
                value: stateLabel,
            });

            // TransDirect API (AU) uses only the short name of state. (e.g. TAS, not Tasmania)
            extraFields.push({
                name: 'stateShort',
                value: addressDetail.stateShort,
            });

            if (addressDetail.addressLineOne) {
                onFieldChange(
                    'addressOne' + billingPostfix,
                    addressDetail.addressLineOne,
                    {
                        extraFields,
                    }
                );
            }

            onFieldChange(
                'streetNumber' + billingPostfix,
                addressDetail.streetNumber || "",
            );
        });
    }, [suggestedAddresses, onFieldChange, onAddressFinder, getAddressDetail, appState.cart?.countryCode, appState.countryCodeByIp, useBilling]);

    useEffect(() => {
        if ((!useBilling && !fields.addressSearch) || (useBilling && !fields.addressSearchBilling)) return;

        clearTimeout(addressChangingTimeout.current);

        addressChangingTimeout.current = setTimeout(() => {
            let addressToUse = useBilling ? fields.addressSearchBilling : fields.addressSearch;
            const countryCode = appState.cart?.countryCode || DEFAULT_COUNTRY_CODE_CONFIG.code;
            if (countryCode.toLowerCase() !== 'nz') {
                addressToUse = addressToUse.replace(/(\d+)\/(\d+)/, "$1, $2")
            }

            getSuggestedAddressesQuery({
                variables: {
                    q: addressToUse,
                    orderCountryCode: countryCode
                },
            }).then(({ data }) => {
                // Don't refocus if an address has actually been selected.
                if (
                    !fields.addressSearch?.length ||
                    !data?.suggestedAddresses?.some(
                        (sa: SuggestedAddress) => sa?.['full_address'] === fields.addressSearch
                    )
                ) {
                    inputRef.current?.focus();
                }
            });
        }, 1200);
        isSearchingAddress.current = false;
    }, [fields.addressSearch, getSuggestedAddressesQuery, fields.addressSearchBilling, useBilling, countryCode.code, appState.cart?.countryCode]);

    const getIsFieldReadOnly = useCallback((fieldName: string) => (
        isReadOnly ||
        readOnlyFields.includes(fieldName) ||
        (countryCode.code && fieldName === 'country' &&  ['nz', 'gb'].includes(countryCode.code.toLowerCase()))
    ), [isReadOnly, readOnlyFields, countryCode]);

    const onAddressChange = (name: string, value: any, otherProps?: OptionalOnFieldChangeArgs) => {
        isSearchingAddress.current = true;
        onFieldChange(name, value, otherProps);
        setShowEmptyAddressMessage(false);
        if (timeoutHolder.current) clearTimeout(timeoutHolder.current);

        timeoutHolder.current = window.setTimeout(() => {
            setShowEmptyAddressMessage(true)
        }, 1300);
    }

    const containerStyle = css`
        width: 100%;

        ${isReadOnly && css`
            pointer-events: none;
            opacity: 0.6;
        `}
    `;

    const labelStyle = ({ isReadOnly = false }) => css`
        label:first-of-type {
            font-size: ${fontSize}px;
            line-height: ${lineHeight}px;
            margin-bottom: -1px;
        }

        > div {
            padding: 0;
        }

        input {
            height: 35px;
            box-shadow: none;

            ${theme.breakpoints.down('lg')} {
                font-size: 16px;
                line-height: 20px;
            }
        }

        ${isReadOnly && css`
            pointer-events: none;
            opacity: 0.6;
        `}
    `;

    const searchableListStyle = css`
        box-shadow: none !important;

        input {
            box-shadow: none !important;
            background-color: ${theme.colours.alabaster};
        }

        .searchable-list-menu {
            top: calc(100% - 5px);
            padding-top: 8px;
            background-color: white;
            box-shadow: ${theme.borderAndShadow.boxShadow};
        }
    `;

    const getInput = (field: string, value: any) => {
        if (field === 'state') {
            return (
                (COUNTRIES_WITH_REGION_OPTIONS[fields?.country] && !isReadOnly) ? (
                    <SearchableList
                        name="state"
                        options={COUNTRIES_WITH_REGION_OPTIONS[fields?.country]}
                        css={searchableListStyle}
                        value={fields[field]}
                        onChange={onFieldChange}
                        placeholder={value.label}
                        hasError={!!errors[field]}
                        valueField={'value'}
                        labelField={'value'}
                        fieldReadOnly={getIsFieldReadOnly(field)}
                        fullHeightForOptions
                        clearable
                    />
                ) : (
                    <Input
                        hasError={!!errors[field]}
                        label={value.label}
                        name={field}
                        onChange={onFieldChange}
                        value={fields[field]}
                        readOnly={getIsFieldReadOnly(field)}
                    />
                )
            );
        } else if (field === 'country' || field === 'countryBilling') {
            return (
                <SearchableList
                    name={field}
                    options={filteredCountries}
                    value={fields[field]}
                    onChange={onFieldChange}
                    labelField="label"
                    valueField="code"
                    placeholder={value.label}
                    hasError={!!errors[field]}
                    variant={'country'}
                    css={searchableListStyle}
                    fieldReadOnly={getIsFieldReadOnly(field)}
                    containsSearchBox
                    clearable
                />
            );
        } else if (field === 'addressSearch' || field === 'addressSearchBilling') {
            return (
                <SearchableList
                    name={field}
                    passedInputRef={inputRef}
                    options={suggestedAddresses || []}
                    value={fields[field]}
                    onChange={onAddressChange}
                    onOptionSelectedFunc={(value) => onSelectedAddressChange(value, field)}
                    labelField="full_address"
                    valueField="full_address"
                    placeholder={'Start typing to find your address...'}
                    hasError={!!errors[field]}
                    css={searchableListStyle}
                    isLoading={suggestedAddressesLoading}
                    showEmptyMessage={showEmptyAddressMessage}
                    showMenuOnValue
                    clearable
                />
            );
        } else if (field === 'phone') {
            return (
                <PhoneNumberInput
                    hasError={!!errors[field] || !!errors.phoneCode}
                    onChange={onFieldChange}
                    phoneNumber={fields[field]}
                    phoneCode={fields.phoneCode}
                    isLoading={isLoading}
                />
            );
        } else if (field === 'city') {
            return (
                <InputWithSelectOptions
                    hasError={!!errors[field]}
                    label={value.label ?? ''}
                    name={field}
                    onChange={onFieldChange}
                    value={fields[field]}
                    readOnly={getIsFieldReadOnly(field)}
                    selectOptions={suburbOptions}
                    setSelectedOption={setSuggestedSuburb}
                    clearSelectOptions={clearSuburbOptions}
                    useSelectOption
                />
            );
        }

        return (
            <Input
                hasError={!!errors[field]}
                label={value.label ?? ''}
                name={field}
                onChange={onFieldChange}
                value={fields[field]}
                readOnly={getIsFieldReadOnly(field)}
            />
        );
    }

    return (
        <div css={containerStyle}>
            {isLoading && (
                <Loading overlayColour={"white"} delay={500} zIndex={theme.zIndex.zIndexHigher} onTop fitToScreen overlay />
            )}
            {Object.entries(ADDITIONAL_FIELD).map(([field, value]) => {
                if (value.isRow) {
                    if (!Object.keys(value.fields).some(key => Object.keys(fields).includes(key))) return null;
                    return (
                        <InputWrapper key={field}>
                            {Object.entries(value.fields).map(([rowField, rowValue]: any) => {
                                const isReadOnly = getIsFieldReadOnly(rowField);
                                if ((useBilling && rowValue.isBilling) || (!useBilling && !rowValue.isBilling)) {
                                    return (
                                        <LabelledField
                                            key={rowField}
                                            name={rowField}
                                            error={errors[rowField]}
                                            label={rowValue.label ?? ''}
                                            css={labelStyle({ isReadOnly })}
                                            defaultAsteriskColour={theme.colours.crimson}
                                            {...hasAsterisk && {
                                                errorAsterisk: rowValue.isRequired
                                            }}
                                        >
                                            {getInput(rowField, rowValue)}
                                        </LabelledField>
                                    )
                                }
                                return null;
                            })}
                        </InputWrapper>
                    );
                }

                if (!Object.keys(fields).includes(field)) return null;

                const isReadOnly = getIsFieldReadOnly(field);
                return (
                    <LabelledField
                        key={field}
                        name={field}
                        error={errors[field]}
                        label={value.label ?? ''}
                        css={labelStyle({ isReadOnly })}
                        defaultAsteriskColour={theme.colours.crimson}
                        {...hasAsterisk && {
                            errorAsterisk: value.isRequired
                        }}                    >
                        {getInput(field, value)}
                    </LabelledField>
                );
            })}
        </div>
    );
};

export default memo(DetailsForm);